import type { Editor } from '@tiptap/react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useEditorStore } from '@/stores/editorStore'
import { useTranslation } from '@/stores/i18nStore'

interface EditorSidebarProps {
  editor: Editor
}

export function EditorSidebar({ editor }: EditorSidebarProps) {
  const { t } = useTranslation()
  const { issues, sidebarTab, setSidebarTab, removeIssue } = useEditorStore()

  const spellingIssues = issues.filter((i) => i.type === 'spelling')
  const grammarIssues = issues.filter((i) => i.type === 'grammar')
  const fidelIssues = issues.filter((i) => i.type === 'fidel')

  const text = editor.getText()
  const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length
  const charCount = text.length
  const amharicChars = (text.match(/[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]/g) || []).length
  const amharicPercent = charCount > 0 ? Math.round((amharicChars / charCount) * 100) : 0
  const readingTime = Math.max(1, Math.ceil(wordCount / 200))

  const handleIssueFix = (issue: typeof issues[0], suggestion: string) => {
    // Navigate to the issue position and replace the text
    editor
      .chain()
      .focus()
      .setTextSelection({ from: issue.position.from, to: issue.position.to })
      .insertContent(suggestion)
      .run()
    removeIssue(issue.id)
  }

  const handleIssueClick = (issue: typeof issues[0]) => {
    editor.chain().focus().setTextSelection(issue.position).run()
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-72 border-l border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 overflow-hidden flex flex-col"
    >
      {/* Tabs */}
      <div className="flex border-b border-zinc-200 dark:border-zinc-800">
        <button
          onClick={() => setSidebarTab('issues')}
          className={cn(
            'flex-1 px-4 py-3 text-sm font-medium transition-colors',
            sidebarTab === 'issues'
              ? 'text-zinc-900 dark:text-white border-b-2 border-zinc-900 dark:border-white'
              : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
          )}
        >
          {t('issues')} ({issues.length})
        </button>
        <button
          onClick={() => setSidebarTab('statistics')}
          className={cn(
            'flex-1 px-4 py-3 text-sm font-medium transition-colors',
            sidebarTab === 'statistics'
              ? 'text-zinc-900 dark:text-white border-b-2 border-zinc-900 dark:border-white'
              : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
          )}
        >
          {t('statistics')}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {sidebarTab === 'issues' ? (
          <div className="space-y-4">
            {issues.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('noIssuesFound')}</p>
              </div>
            ) : (
              <>
                {/* Spelling issues */}
                {spellingIssues.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      {t('spelling')} ({spellingIssues.length})
                    </h3>
                    <div className="space-y-2">
                      {spellingIssues.map((issue) => (
                        <IssueCard
                          key={issue.id}
                          issue={issue}
                          onClick={() => handleIssueClick(issue)}
                          onFix={(suggestion) => handleIssueFix(issue, suggestion)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Grammar issues */}
                {grammarIssues.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {t('grammar')} ({grammarIssues.length})
                    </h3>
                    <div className="space-y-2">
                      {grammarIssues.map((issue) => (
                        <IssueCard
                          key={issue.id}
                          issue={issue}
                          onClick={() => handleIssueClick(issue)}
                          onFix={(suggestion) => handleIssueFix(issue, suggestion)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Fidel issues */}
                {fidelIssues.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500" />
                      {t('fidelForm')} ({fidelIssues.length})
                    </h3>
                    <div className="space-y-2">
                      {fidelIssues.map((issue) => (
                        <IssueCard
                          key={issue.id}
                          issue={issue}
                          onClick={() => handleIssueClick(issue)}
                          onFix={(suggestion) => handleIssueFix(issue, suggestion)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <StatItem label={t('words')} value={wordCount.toLocaleString()} />
            <StatItem label={t('characters')} value={charCount.toLocaleString()} />
            <StatItem label={t('amharicPercent')} value={`${amharicPercent}%`} />
            <StatItem label={t('readingTime')} value={`${readingTime} min`} />
            <StatItem label={t('paragraphs')} value={editor.getJSON().content?.length.toString() || '0'} />
          </div>
        )}
      </div>
    </motion.div>
  )
}

function IssueCard({
  issue,
  onClick,
  onFix
}: {
  issue: {
    id: string
    type: string
    text: string
    message: string
    suggestions: string[]
  }
  onClick: () => void
  onFix: (suggestion: string) => void
}) {
  return (
    <div
      onClick={onClick}
      className="p-3 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 cursor-pointer hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors"
    >
      <p className="text-sm font-medium text-zinc-900 dark:text-white font-ethiopic mb-1">
        "{issue.text}"
      </p>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">{issue.message}</p>
      {issue.suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {issue.suggestions.slice(0, 2).map((suggestion, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation()
                onFix(suggestion)
              }}
              className="px-2 py-1 text-xs font-medium rounded bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors font-ethiopic"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
      <span className="text-sm text-zinc-500 dark:text-zinc-400">{label}</span>
      <span className="text-sm font-medium text-zinc-900 dark:text-white">{value}</span>
    </div>
  )
}
